import { Card, CardContent, CardHeader, CardTitle } from '@respectly/ui';
import type { FunnelStage } from '@respectly/shared-types';
import { ArrowDown } from 'lucide-react';

interface CompletionFunnelCardProps {
  featureName: string;
  stages: FunnelStage[];
  color: string;
  isLoading?: boolean;
}

export function CompletionFunnelCard({
  featureName,
  stages,
  color,
  isLoading,
}: CompletionFunnelCardProps) {
  if (isLoading) {
    return (
      <Card className="border-slate-200/60 dark:border-slate-700/60 flex-1">
        <CardHeader className="pb-1 pt-4 px-4">
          <div className="h-3 w-20 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
        </CardHeader>
        <CardContent className="px-4 pb-4 space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-10 bg-slate-100 dark:bg-slate-800 rounded animate-pulse" />
          ))}
        </CardContent>
      </Card>
    );
  }

  const maxCount = Math.max(...stages.map((s) => s.count), 1);
  const totalCompleted = stages[stages.length - 1]?.count ?? 0;
  const totalStarted = stages[0]?.count ?? 0;
  const overallRate = totalStarted > 0 ? Math.round((totalCompleted / totalStarted) * 100) : 0;

  return (
    <Card className="border-slate-200/60 dark:border-slate-700/60 flex-1">
      <CardHeader className="pb-2 pt-4 px-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
            {featureName}
          </CardTitle>
          <span
            className="text-xs font-bold tabular-nums px-1.5 py-0.5 rounded"
            style={{ color, backgroundColor: `${color}18` }}
          >
            {overallRate}%
          </span>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        <div className="space-y-2">
          {stages.map((stage, index) => {
            const widthPct = maxCount > 0 ? Math.max((stage.count / maxCount) * 100, 4) : 4;
            const isLast = index === stages.length - 1;

            return (
              <div key={stage.label}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium truncate">
                    {stage.label}
                  </span>
                  <span className="text-[10px] font-bold tabular-nums text-slate-700 dark:text-slate-300 ml-1 shrink-0">
                    {stage.count.toLocaleString()}
                  </span>
                </div>
                <div className="h-5 bg-slate-100 dark:bg-slate-800/80 rounded-sm overflow-hidden">
                  <div
                    className="h-full rounded-sm transition-all"
                    style={{ width: `${widthPct}%`, backgroundColor: color, opacity: isLast ? 1 : 0.55 + (index * 0.15) }}
                  />
                </div>
                {!isLast && stage.dropOffRate !== undefined && stage.dropOffRate > 0 && (
                  <div className="flex items-center gap-1 mt-1">
                    <ArrowDown className="h-2.5 w-2.5 text-red-400" />
                    <span className="text-[9px] text-red-400 font-medium">{stage.dropOffRate}% drop-off</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {totalStarted === 0 && (
          <p className="text-[10px] text-slate-400 dark:text-slate-500 text-center mt-2">
            No activity yet
          </p>
        )}
      </CardContent>
    </Card>
  );
}
